/**
 * RecordCallToggle  (ADDITIVE — INCOMING RECORDING, per-Call-SID)
 *
 * Small self-contained toggle rendered inside IncomingCallModal while
 * the call is ringing. Local, additive, does not read any workspace
 * outgoing-recording setting — the incoming default is deliberately
 * OFF (matches the "safe default" clause of the spec).
 *
 * NEVER decides whether recording actually starts; it only reports the
 * user's intent. The provider owns the per-Call-SID preference map and
 * routes it to the same user's other surface via realtime + starts the
 * recording after accept.
 */
import { Mic, MicOff } from 'lucide-react';

export interface RecordCallToggleProps {
  /** Current selection. */
  record: boolean;
  /** Fires when the user flips the switch. */
  onChange: (next: boolean) => void;
  /** True after accept has begun — the toggle is locked in from here. */
  disabled?: boolean;
}

export function RecordCallToggle({ record, onChange, disabled = false }: RecordCallToggleProps) {
  const label = `Record Call: ${record ? 'ON' : 'OFF'}`;
  return (
    <div
      className="mt-4 flex w-full items-center justify-between rounded-lg bg-gray-50 px-3 py-2"
      data-testid="incoming-record-toggle-row"
    >
      <div className="flex items-center gap-2 text-sm text-gray-700">
        {record ? (
          <Mic className="h-4 w-4 text-red-600" aria-hidden="true" />
        ) : (
          <MicOff className="h-4 w-4 text-gray-400" aria-hidden="true" />
        )}
        <span className="font-medium">Record Call</span>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={record}
        aria-label={label}
        onClick={() => onChange(!record)}
        disabled={disabled}
        data-testid="incoming-record-toggle"
        className={[
          'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors',
          record ? 'bg-red-600' : 'bg-gray-300',
          disabled ? 'cursor-not-allowed opacity-50' : '',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2',
        ].join(' ')}
      >
        <span
          className={[
            'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
            record ? 'translate-x-5' : 'translate-x-0.5',
          ].join(' ')}
          aria-hidden="true"
        />
      </button>
    </div>
  );
}

export default RecordCallToggle;
